/**
 * Cross-browser requestIdleCallback with fallback
 *
 * Uses native requestIdleCallback when available, falls back to setTimeout.
 * Useful for deferring non-critical work until the browser is idle.
 *
 * @example
 * ```typescript
 * import { requestIdleCallback, cancelIdleCallback } from '~/utils/requestIdleCallback';
 *
 * const id = requestIdleCallback(() => {
 *   // Non-critical work here
 * }, { timeout: 5000 });
 *
 * // Cancel if needed
 * cancelIdleCallback(id);
 * ```
 */

type IdleRequestCallback = (deadline: IdleDeadline) => void;

interface IdleRequestOptions {
  timeout?: number;
}

interface IdleDeadline {
  didTimeout: boolean;
  timeRemaining: () => number;
}

// Check if native requestIdleCallback is available
const hasNativeSupport =
  typeof window !== 'undefined' && 'requestIdleCallback' in window;

/**
 * Request a callback when the browser is idle.
 *
 * @param callback - Function to call when idle
 * @param options - Options object with optional timeout
 * @returns ID that can be used to cancel the callback
 */
export function requestIdleCallback(
  callback: IdleRequestCallback,
  options?: IdleRequestOptions
): number {
  if (hasNativeSupport) {
    return window.requestIdleCallback(callback, options);
  }

  // Fallback: use setTimeout with a simulated deadline
  const timeout = options?.timeout ?? 1000;
  const start = Date.now();

  return window.setTimeout(() => {
    callback({
      didTimeout: true,
      timeRemaining: () => Math.max(0, 50 - (Date.now() - start))
    });
  }, timeout) as unknown as number;
}

/**
 * Cancel a previously scheduled idle callback.
 *
 * @param id - The ID returned by requestIdleCallback
 */
export function cancelIdleCallback(id: number): void {
  if (hasNativeSupport) {
    window.cancelIdleCallback(id);
  } else {
    window.clearTimeout(id);
  }
}
